import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { SEO } from "../components/SEO";
import { PrimaryButton, WhatsAppButton } from "../components/Buttons";
import { BlogContent } from "../components/blog/BlogContent";
import { PostCard } from "../components/blog/PostCard";
import NotFoundPage from "./NotFoundPage";
import { SITE } from "../data/site";
import { waLink } from "../lib/whatsapp";
import { useGoToSection } from "../lib/useGoToSection";
import {
    BLOG_PATH,
    articleJsonLd,
    formatDate,
    getPost,
    getPosts,
    ogImagePath,
    postPath,
    relatedPosts,
    stageNeighbours,
} from "../lib/blog";

export const BLOG_TITLE = `Blog — Local Business Growth Insights | ${SITE.name}`;
export const BLOG_DESCRIPTION =
    "Practical ideas for local businesses in Kolkata: getting found on Google, turning enquiries into bookings with WhatsApp, and growing with content and ads.";

export function BlogIndexPage() {
    const posts = getPosts();
    return (
        <>
            <SEO title={BLOG_TITLE} description={BLOG_DESCRIPTION} path={BLOG_PATH} />
            <Navbar />
            <main className="pb-24 pt-32 sm:pt-40" data-testid="blog-index">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                    <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-brand-orange">The 033 Blog</p>
                    <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold tracking-tight text-brand-brown sm:text-5xl">
                        Practical growth ideas for local businesses.
                    </h1>
                    <p className="mt-5 max-w-2xl text-base leading-relaxed text-brand-muted sm:text-lg">
                        How nearby customers find you, contact you, book you, review you and come back — and how to stop losing
                        them along the way.
                    </p>
                    <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {posts.map((p) => (
                            <PostCard key={p.slug} post={p} />
                        ))}
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
}

const PostCTA = ({ cta }) => {
    const goTo = useGoToSection();
    const button = (b, i) =>
        b.action === "scan" ? (
            <PrimaryButton key={i} onClick={() => goTo("scan")} testid="blog-cta-scan">
                {b.label}
            </PrimaryButton>
        ) : (
            <WhatsAppButton key={i} href={waLink(b.message)} testid="blog-cta-whatsapp" dark>
                {b.label}
            </WhatsAppButton>
        );
    return (
        <aside className="mt-14 rounded-3xl bg-brand-brown-deep p-7 sm:p-10" data-testid="blog-cta">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand-orange">Next step</p>
            <p className="mt-3 font-display text-2xl font-bold leading-snug text-brand-paper sm:text-3xl">{cta.lead}</p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                {[cta.primary, cta.secondary].filter(Boolean).map(button)}
            </div>
        </aside>
    );
};

const STAGE_NAMES = { 1: "Get Found", 2: "Get Booked", 3: "Get Growing" };

const SeriesNav = ({ post }) => {
    const { prev, next } = stageNeighbours(post);
    if (!prev && !next) return null;
    return (
        <nav className="mt-10 grid gap-4 sm:grid-cols-2" aria-label="033 System series" data-testid="blog-series-nav">
            {prev ? (
                <Link to={postPath(prev.slug)} className="rounded-2xl border border-brand-brown/10 bg-white p-5 transition-colors hover:border-brand-orange">
                    <span className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-brand-muted">
                        <ArrowLeft size={13} aria-hidden="true" /> Stage 0{prev.stage} · {STAGE_NAMES[prev.stage]}
                    </span>
                    <span className="mt-2 block font-display text-lg font-bold text-brand-brown">{prev.title}</span>
                </Link>
            ) : (
                <span className="hidden sm:block" />
            )}
            {next && (
                <Link to={postPath(next.slug)} className="rounded-2xl border border-brand-brown/10 bg-white p-5 text-right transition-colors hover:border-brand-orange">
                    <span className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-brand-orange">
                        Next: Stage 0{next.stage} · {STAGE_NAMES[next.stage]} <ArrowRight size={13} aria-hidden="true" />
                    </span>
                    <span className="mt-2 block font-display text-lg font-bold text-brand-brown">{next.title}</span>
                </Link>
            )}
        </nav>
    );
};

export function BlogPostPage() {
    const { slug } = useParams();
    const post = getPost(slug);
    if (!post) return <NotFoundPage />;

    return (
        <>
            <SEO
                title={`${post.title} | ${SITE.name}`}
                description={post.description}
                path={postPath(post.slug)}
                image={ogImagePath(post.slug)}
                type="article"
                jsonLd={articleJsonLd(post)}
            />
            <Navbar />
            <main className="pb-24 pt-32 sm:pt-40" data-testid={`blog-post-${post.slug}`}>
                <article className="mx-auto max-w-3xl px-4 sm:px-8">
                    <Link
                        to={BLOG_PATH}
                        data-testid="blog-back-link"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-brand-orange transition-colors duration-200 hover:text-brand-orange-dark"
                    >
                        <ArrowLeft size={15} aria-hidden="true" />
                        All articles
                    </Link>
                    <p className="mt-8 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-brand-muted">
                        <span className="text-brand-orange">{post.category}</span> · {formatDate(post.date)} · {post.readMinutes} min read
                    </p>
                    <h1 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-brand-brown sm:text-5xl">
                        {post.title}
                    </h1>
                    <p className="mt-5 text-lg leading-relaxed text-brand-muted">{post.description}</p>
                    <img
                        src={post.cover}
                        alt={post.coverAlt}
                        width="1024"
                        height="765"
                        className="mt-10 aspect-[4/3] w-full rounded-3xl object-cover"
                    />
                    <div className="mt-10">
                        <BlogContent blocks={post.blocks} />
                    </div>
                    <SeriesNav post={post} />
                    <PostCTA cta={post.cta} />
                </article>

                <section className="mx-auto mt-20 max-w-7xl px-4 sm:px-8 lg:px-16" aria-labelledby="related-heading">
                    <h2 id="related-heading" className="font-display text-2xl font-bold tracking-tight text-brand-brown sm:text-3xl">
                        More from the blog
                    </h2>
                    <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {relatedPosts(post).map((p) => (
                            <PostCard key={p.slug} post={p} />
                        ))}
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
